import React, { useRef, useState } from 'react';
import { Box } from '@components/Box';
import {
  BlogCenterTextPairing,
  MainBackgroundImage,
  StyledBlogWrapper,
  PaymentLogo,
  BottomWaveImage,
  StyledVideo,
  StyledVideoWrapper,
  StyledMobileSection,
  BlogShape1Mobile,
  BlogShape2Mobile,
  LogosFlex,
  StyledBlogShape,
  Fee2LogosWrapper,
  Fee2LogosWrapperAndTextWrapper,
  Fee2LogoImageWrapper,
  ShadowBox,
  Fee2CloudImage,
  StyledBox,
  StyledFee2Text,
  StyledLogoText,
  DownButtonWrapper,
} from './RegistryCashFreeGiftingSection.styles';
import { registryPageData } from '@data-ts/pages/registry';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { useOnScreen } from '@hooks/useOnScreen';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { defaultBase64Placeholder } from '@components/CloudImage';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { CTALink } from '@components/CTALink';

const registryCashFreeGiftingData = registryPageData.cashFreeGiftingSection;

interface RegistryCashFreeGiftingSectionProps {}

export const RegistryCashFreeGiftingSection: React.FC<RegistryCashFreeGiftingSectionProps> = () => {
  const CashFreeGiftingRef = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(CashFreeGiftingRef, '0px', true);
  const isMobile = useThemeMediaQuery('below_sm');
  const isXLDevice = useThemeMediaQuery('xl');
  /**
   *
   * @returns url of the video based on device resolution
   */
  const getVideoURLForDevice = () => {
    if (isMobile) {
      return 'https://player.vimeo.com/progressive_redirect/playback/525873936/rendition/360p/file.mp4?loc=external&signature=58b53d810300cac87e836b85e8ce40ee267854ae5eb37edf1ddccda6a4828a71';
    }
    if (isXLDevice) {
      return 'https://player.vimeo.com/progressive_redirect/playback/525873936/rendition/1080p/file.mp4?loc=external&signature=5388df13bde6767ca530f4444b04c9786c29986f5e33670f6444c0877116bbdb';
    }
    return 'https://player.vimeo.com/progressive_redirect/playback/525873936/rendition/720p/file.mp4?loc=external&signature=4201a262f77712c6728aa553019f464f764154d92a82c45d5810f16085578441';
  };
  let [isScrolledIntoView, setScrolledIntoView] = useState(false);

  return (
    <StyledMobileSection as="section" id="Cash_Funds" ref={CashFreeGiftingRef}>
      {onScreen ? (
        <StyledVideoWrapper>
          <StyledVideo
            muted
            loop
            autoPlay
            preload="auto"
            src={getVideoURLForDevice()}
            poster={cloudAssetUrl('registry-v2/zero-fee-cash-gifting/image-zero-fee-2-1-v3.jpg')}
            playsInline
          />
        </StyledVideoWrapper>
      ) : null}
      <StyledBlogShape justifyContent="center">
        <StyledBlogWrapper>
          <BlogShape1Mobile src={'registry-v2/zero-fee-cash-gifting/gold-thread-4.svg'} />
          <BlogShape2Mobile
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            backgroundImage={'registry-v2/zero-fee-cash-gifting/zero-fee-shape.svg'}
          >
            <StyledBox alignItems="center">
              <BlogCenterTextPairing
                alignment="center"
                eyebrow={registryCashFreeGiftingData.blogEyebrow}
                eyebrowColor="accentBlue4"
                eyebrowVariant="sectionEyeBrow20"
                eyebrowTagName="h2"
                title={registryCashFreeGiftingData.blogTitle}
                titleVariant="hed56"
                titleColor="accentBlue5"
                titleTagName="h2"
                body={registryCashFreeGiftingData.blogBody}
                bodyVariant="dek24"
                bodyColor="neutra6"
                bodyTagName="p"
              />
              <StyledLogoText textAlign="center" marginTop={[6]} tagName="p" variant="sectionEyeBrow20" color="neutra6">
                {registryCashFreeGiftingData.blogFooterNote}
              </StyledLogoText>
              <LogosFlex paddingTop={[8]} alignItems="flex-end">
                <PaymentLogo alt="Venmo cash registry" src="registry-v2/zero-fee-cash-gifting/logo-venmo.svg" />
                <PaymentLogo alt="CashApp cash registry" src="registry-v2/zero-fee-cash-gifting/logo-cashapp.svg" />
                <PaymentLogo alt="PayPal cash registry" src="registry-v2/zero-fee-cash-gifting/logo-paypal.svg" />
              </LogosFlex>
              <DownButtonWrapper style={{ textAlign: 'center' }} marginTop={[8, 7]}>
                <Box marginRight={[8, 10]}>
                  <GetStartedButton id="registryCashFreeGetStartedBtn" page={'registry'} variant="secondary">
                    {registryCashFreeGiftingData.getStarted}
                  </GetStartedButton>
                </Box>
                <Box marginTop={[4, 3]}>
                  <CTALink
                    id="registryCashFreeLearnMoreBtn"
                    hasChevron={true}
                    aria-label="Cash Fund Registry"
                    href={registryCashFreeGiftingData.learnMoreLink}
                  >
                    {registryCashFreeGiftingData.learnMore}
                  </CTALink>
                </Box>
              </DownButtonWrapper>
            </StyledBox>
          </BlogShape2Mobile>
        </StyledBlogWrapper>
      </StyledBlogShape>
      <MainBackgroundImage
        backgroundImage={isScrolledIntoView ? 'registry-v2/zero-fee-cash-gifting/image-zero-fee-2.jpg' : defaultBase64Placeholder}
      >
        <LazyLoadWrapper beforeLoad={() => setScrolledIntoView(true)}>
          <div />
        </LazyLoadWrapper>
        <Fee2LogosWrapperAndTextWrapper marginTop={[14, 14, 15]} flexDirection="column" alignItems="center">
          <StyledFee2Text textAlign="center" tagName="h2" variant="calloutHed32" color="accentBlue6">
            {registryCashFreeGiftingData.fee2BlogText}
          </StyledFee2Text>
          <Fee2LogosWrapper marginTop={[8, 10]} backgroundColor="accentBlue1">
            <Fee2LogoImageWrapper alignItems="center" justifyContent="center">
              <Fee2CloudImage src="registry-v2/zero-fee-cash-gifting/logo-theknot-v3.svg" />
            </Fee2LogoImageWrapper>
            <Fee2LogoImageWrapper alignItems="center" justifyContent="center">
              <Fee2CloudImage src="registry-v2/zero-fee-cash-gifting/logo-honeyfund-v3.svg" />
            </Fee2LogoImageWrapper>
            <Fee2LogoImageWrapper alignItems="center" justifyContent="center">
              <ShadowBox alignItems="center" justifyContent="center">
                <Fee2CloudImage src="registry-v2/zero-fee-cash-gifting/logo-joy-v2.svg" />
              </ShadowBox>
            </Fee2LogoImageWrapper>
            <Fee2LogoImageWrapper alignItems="center" justifyContent="center">
              <Fee2CloudImage src="registry-v2/zero-fee-cash-gifting/logo-travelersjoy-v4.svg" />
            </Fee2LogoImageWrapper>
            <Fee2LogoImageWrapper alignItems="center" justifyContent="center">
              <Fee2CloudImage src="registry-v2/zero-fee-cash-gifting/logo-myreg.svg" />
            </Fee2LogoImageWrapper>
          </Fee2LogosWrapper>
        </Fee2LogosWrapperAndTextWrapper>
      </MainBackgroundImage>
      {/* <BottomWaveImage src="registry-v2/zero-fee-cash-gifting/zero-fee-bottom-wave.svg" /> */}
    </StyledMobileSection>
  );
};
